import { Fragment, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useAppContext, useScrollRestore } from 'hooks';

import {
  ProjectContainer,
  ProjectSection,
  ProjectSectionContent,
  ProjectTextRow,
  ProjectImage,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectHeader,
} from 'components/ProjectLayout';
import ThemeProvider, { useTheme } from 'components/ThemeProvider';
import SegmentedControl, { SegmentedControlOption } from 'components/SegmentedControl';
import { media } from 'utils/style';
import imageSprLessonBuilderLightPlaceholder from 'assets/spr-lesson-builder-light-placeholder.jpg';
import imageSprLessonBuilderDark from 'customAssets/wmlWebsitePreview.jpg';
import imageSprLessonBuilderDarkLarge from 'customAssets/wmlWebsitePreview.jpg';
import imageSprLessonBuilderDarkPlaceholder from 'assets/spr-lesson-builder-dark-placeholder.jpg';

import Earth, { EarthSection } from './Earth';
import './index.css';
import { CustomCaraousel } from './carousel';
import { EndFooter } from 'pages/Home/footer/footer';
import { appJoxDescription, appJoxTitle } from 'pages/Home';

const title = 'Wicked Maine Lobster';
const description =
  'Wicked Maine Lobster is a popular California-based restaurant chain. I lead the development of its food ordering system.';
const roles = [
  'Product Architecture',
  'UX and UI Design',
  'Front End Development',
  'Back End Development',
  'Deployment',
];

const WMLWeb = () => {
  const { themeId } = useTheme();
  const { dispatch } = useAppContext();
  const earthSectionRef = useRef();
  useScrollRestore();

  const isDark = themeId === 'dark';
  const themes = ['dark', 'light'];

  const handleThemeChange = index => {
    dispatch({ type: 'setTheme', value: themes[index] });
  };

  return (
    <Fragment>
      <ProjectContainer className="spr">
        <Helmet>
          <title>{`Projects | ${title}`}</title>
          <meta name="description" content={description} />{' '}
          <meta name="og:description" content={description} />
          <meta
            property="og:image"
            content="https://i.ibb.co/WtQsRdm/Screenshot-2022-02-15-204105.png"
          />{' '}
          <meta property="og:title" content={`Projects | ${title}`} />
          <meta name="twitter:description" content={appJoxDescription} />
          <meta name="twitter:title" content={appJoxTitle} />
        </Helmet>

        <ProjectHeader
          title={title}
          description={description}
          url="https://wickedmainelobster.com/"
          roles={roles}
        />
        <ProjectSection first>
          <ProjectSectionContent>
            <ProjectImage
              raised
              key={themeId}
              srcSet={`${imageSprLessonBuilderDark} 1280w, ${imageSprLessonBuilderDarkLarge} 2560w`}
              placeholder={
                isDark
                  ? imageSprLessonBuilderDarkPlaceholder
                  : imageSprLessonBuilderLightPlaceholder
              }
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt="Wicked Maine Lobster Website"
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectTextRow>
            <ProjectSectionHeading>The problem</ProjectSectionHeading>
            <ProjectSectionText>
              In January 2021, the owner of Wicked Maine Lobster, Mr. Alex Howard,
              started the project to transfer his Shopify website to his own custom
              website. He wanted both the mobile apps and websites connected seamlessly
              with each other. He also wanted a management app to manage the system
              through his phone.
            </ProjectSectionText>
          </ProjectTextRow>
        </ProjectSection>
        <ProjectSection light={isDark}>
          <ProjectSectionContent>
            <CustomCaraousel />

            <ProjectTextRow>
              <SegmentedControl
                currentIndex={themes.indexOf(themeId)}
                onChange={handleThemeChange}
              >
                <SegmentedControlOption>Dark theme</SegmentedControlOption>
                <SegmentedControlOption>Light theme</SegmentedControlOption>
              </SegmentedControl>
            </ProjectTextRow>
            <ProjectTextRow>
              <ProjectSectionHeading>Our solution</ProjectSectionHeading>
              <ProjectSectionText>
                We analyzed the problem and suggested to him our complete ordering
                solution. He loved our suggestion! 90 days later, we delivered him the
                best food ordering solution. The solution contained 3 Mobile Applications
                (Android & iOS) and a website. Today, the website itself is generating
                million dollar revenue and handling 10k visitors/week.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <EndFooter />
    </Fragment>
  );
};

export default WMLWeb;
